import React, { useState, useEffect } from 'react';
import { ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SponsorHeroBanner = () => {
  const [sponsors, setSponsors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    const fetchSponsors = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/cms/sponsor-heroes`);
        if (response.ok) {
          const data = await response.json();
          setSponsors(data);
        }
      } catch (err) {
        console.error('Failed to fetch sponsor heroes:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSponsors();
  }, []);

  // Detect theme mode
  useEffect(() => {
    const checkTheme = () => {
      const theme = localStorage.getItem('theme') || 'dim';
      setIsDarkMode(theme === 'dark' || theme === 'dim');
    };
    
    checkTheme();
    window.addEventListener('storage', checkTheme);
    
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    
    return () => {
      window.removeEventListener('storage', checkTheme);
      observer.disconnect();
    };
  }, []);

  // Get the appropriate logo URL based on theme
  const getLogoUrl = (sponsor) => {
    if (isDarkMode) {
      return sponsor.logo_url_dark || sponsor.logo_url;
    }
    return sponsor.logo_url_light || sponsor.logo_url;
  };

  // Don't render if no sponsors or still loading
  if (isLoading || sponsors.length === 0) return null;

  return (
    <div 
      className="fixed top-0 left-0 right-0 z-[60] bg-secondary border-b border-primary/20 py-2"
      data-testid="sponsor-hero-banner"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-center gap-6 flex-wrap">
          <span className="text-[10px] uppercase tracking-[0.2em] text-primary/80 font-medium whitespace-nowrap">
            Proudly Hosted By
          </span>
          <div className="flex items-center gap-4 flex-wrap justify-center">
            {sponsors.map((sponsor) => (
              <a
                key={sponsor.id}
                href={sponsor.website || '#'}
                target={sponsor.website ? "_blank" : undefined}
                rel="noopener noreferrer"
                className="group flex items-center gap-1.5 opacity-80 hover:opacity-100 transition-opacity"
                title={sponsor.name}
                data-testid={`sponsor-hero-${sponsor.id}`}
              >
                {getLogoUrl(sponsor) ? (
                  <img 
                    src={getLogoUrl(sponsor)} 
                    alt={sponsor.name} 
                    className="h-5 w-auto object-contain max-w-[100px]"
                  />
                ) : (
                  <span className="text-xs font-semibold text-white/90 group-hover:text-white tracking-wide">
                    {sponsor.name}
                  </span>
                )}
                {sponsor.website && (
                  <ExternalLink className="w-2.5 h-2.5 text-primary/60 opacity-0 group-hover:opacity-100 transition-opacity" />
                )}
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SponsorHeroBanner;
